import { useState } from "react";
import { toast } from "sonner";
import { Key, SignOut, EnvelopeSimple } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatApiErrorDetail } from "@/lib/format";
import { heroImg } from "@/lib/meta";

export default function LicenseGate() {
  const { user, redeemLicense, logout } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await redeemLicense(code.trim());
      toast.success("License activated — welcome!");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain relative min-h-screen flex items-center justify-center px-4 py-10">
      <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${heroImg})` }} />
      <div className="absolute inset-0 bg-background/85" />

      <div className="relative w-full max-w-md border border-border bg-card p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Thermal Spray Atlas" className="h-9 w-9 rounded-sm object-cover" />
            <div className="leading-none">
              <div className="font-heading font-black text-white text-sm tracking-tight">THERMAL SPRAY ATLAS</div>
              <div className="font-mono text-[10px] text-muted-foreground tracking-widest">LICENSE REQUIRED</div>
            </div>
          </div>
          <Button data-testid="license-logout-btn" onClick={logout} variant="ghost" size="sm" className="rounded-sm text-muted-foreground hover:text-primary">
            <SignOut size={16} weight="bold" />
          </Button>
        </div>

        <div className="flex h-12 w-12 items-center justify-center bg-primary rounded-sm mb-5">
          <Key size={26} weight="fill" className="text-white" />
        </div>
        <h1 className="font-heading font-black text-white text-2xl">Activate your access</h1>
        <p className="text-sm text-muted-foreground mt-2 mb-6">
          Signed in as <span className="text-secondary font-mono">{user?.email}</span>. Enter the unique license number
          provided by the administrator to start using Thermal Spray Atlas.
        </p>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">License Number</label>
            <Input
              data-testid="license-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="TSA-XXXX-XXXX-XXXX"
              className="mt-1.5 rounded-sm bg-background border-border font-mono text-sm tracking-widest focus-visible:ring-primary"
            />
          </div>
          {error && <div data-testid="license-error" className="text-xs text-destructive border border-destructive/40 bg-destructive/10 px-3 py-2 rounded-sm">{error}</div>}
          <Button data-testid="license-submit" type="submit" disabled={busy || !code.trim()} className="w-full rounded-sm bg-primary hover:bg-primary/90 text-white font-mono uppercase tracking-widest text-xs h-11">
            {busy ? "Activating..." : "Proceed"}
          </Button>
        </form>

        <div className="mt-6 pt-5 border-t border-border flex items-center gap-2 text-xs text-muted-foreground">
          <EnvelopeSimple size={15} className="text-secondary shrink-0" />
          <span>For a license, please contact <a href="mailto:info@metallization.in" className="text-secondary hover:underline">info@metallization.in</a></span>
        </div>
      </div>
    </div>
  );
}
